import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import IconToolTip from '../../../common/IconToolTip';
import OverflowTip from '../../../common/OverflowTip';
import UserPopup from '../../../common/Popups/User';
import { copyToClipboard } from '../../../actions/utils';
import { useDispatch } from 'react-redux';
import { getAssetColor, isLogged, ScogoID, validateStringValue } from '../../../utils/common';
import ButtonScogoOrange from '../../../common/Buttons/ButtonScogoOrange';
import { LineClampComponent } from '../TicketTableComponent';
import moment from 'moment';
import AssetPopup from '../../../common/Popups/AssetPop';
import { closeModalAction, openCustomModal } from '../../../actions/modal';
import ViewAllRemark from '../Modal/ViewAllRemark';
import { SpanLabelButton } from '../../UserProfile/BusinessDetails/CustomerBusinessDetails';
import PaymentsDetails from '../../Payments/Modals/PaymentsDetails';
import { convertIntoRupee } from '../../../utils/utils';
import { canEditTicket, canViewCustomerBillingTicket, canViewIncentiveTicket, eligibleForSiteComplete, isMilestoneStatusInRange, MilestoneSubStatus, MilestoneType, openPavReportModal, TicketAgentRole } from '../TicketController';
import { isPavMilestone } from '../../../features/pav/pavConfig';
import { generateSignoffReport, generateTicketReport } from '../../../actions/service_request';
import MemberList from '../../chat/sidedrawer/Components/MemberList';
import ModifyNotificationSettings from '../../chat/sidedrawer/Components/ModifyNotificationSettings';
import CoversationMedia from '../../chat/sidedrawer/Media';
import { RenderFullSideViewMedia } from '../../chat/sidedrawer';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import SiteComplete from '../Modal/SiteComplete';


const Details = ({ serviceRequest, showMembers = [], groupDetailsInfo, isLoggedUserIsAdmin, isTicketChat = false, setShowMedia }) => {
    const dispatch = useDispatch();
    const serviceRequestList = useSelector((state) => state.serviceRequest.serviceRequestList);

    const memoizedServiceRequest = useMemo(() => {
        let req = serviceRequest;
        if (Array.isArray(serviceRequestList?.data) && serviceRequestList.data.length > 0) {
            const find = serviceRequestList.data.find((elem) => elem.id === serviceRequest.id);
            if (find) req = find;
        }
        return req;
    }, [serviceRequest, serviceRequestList]);

    const newLoggedUserData = useSelector(state => state.auth.newLoggedUserData);
    const isLoginUserIsFE = newLoggedUserData?.user?.roles?.find((elem) => elem.label === 'FE')
    const isSameOrgTicket = newLoggedUserData.orgMemberships.find(({ id }) => id === serviceRequest.helpdesk_id);
    const onSiteCompleteClick = () => {
        let heading = 'Site Completed';
        const modalComponent = <SiteComplete ticketId={serviceRequest.id} milestones={serviceRequest.milestones} agents={serviceRequest.agents} />;
        let modalHeight = 'auto',
            modalWidth = '45rem';
        dispatch(openCustomModal({ modalComponent, heading, modalHeight, modalWidth }));
    }

    return (
        <div className='px-5 py-4'>
            <div className='mt-2'>
                <RenderContact organization={serviceRequest.organization} agents={serviceRequest.agents} isCollapsible={true} />
            </div>
            {canEditTicket(serviceRequest) && eligibleForSiteComplete(serviceRequest.milestones, serviceRequest.status) && (
                <div className='flex justify-center items-center mt-4'>
                    <ButtonScogoPrimary
                        textOrComponent='Site Complete'
                        onClick={onSiteCompleteClick}
                    />
                </div>
            )}
            <div className='mt-4'>
                <RenderBasicDetails serviceRequest={memoizedServiceRequest} />
            </div>

            {isTicketChat && <div className='mt-4 bg-white'>
                <ModifyNotificationSettings
                    conversationId={serviceRequest.id}
                    sendNotificationsFor={groupDetailsInfo?.notification_mode}
                    mainDivClass='p-5 px-6 bg-white my-3 rounded-md shadow-md'
                />
                <CoversationMedia mainDivClass='p-5 px-6 border-t-10 w-full' setShowMedia={setShowMedia}
                    conversationMedia showCustomerReport />
                <MemberList
                    conversationId={serviceRequest.id}
                    detailsInfo={groupDetailsInfo}
                    isLoggedUserIsAdmin={isLoggedUserIsAdmin}
                />
            </div>}
            {(isSameOrgTicket && !isLoginUserIsFE) ? <RenderPaymentDetails serviceRequest={memoizedServiceRequest} /> : null}
        </div>
    );
};

export default Details;



const RenderContact = ({ organization, agents, isCollapsible = false }) => {
    const dispatch = useDispatch();
    const isLoggedIn = isLogged();

    const memoizedAgents = useMemo(() => {
        let list = [];
        if (organization) {
            list.push({ role: 'CUSTOMER', agent: { display_name: organization.name } });
        }

        if (agents) {
            // filter for relevant roles
            let filteredAgents = agents.filter(({ role }) =>
                [
                    TicketAgentRole.CLUSTER,
                    TicketAgentRole.PSP,
                    TicketAgentRole.SUPERVISOR,
                    TicketAgentRole.PM,
                    TicketAgentRole.SP,
                    TicketAgentRole.FE
                ].includes(role)
            );

            // Group all agents by agent.id for deduplication of SP and FE
            const agentById = {};
            filteredAgents.forEach((agentItem) => {
                const agentId = agentItem.agent.id;
                if (!agentById[agentId]) {
                    agentById[agentId] = [];
                }
                agentById[agentId].push(agentItem);
            });

            const dedupedAgents = [];

            Object.values(agentById).forEach((agentRoles) => {
                // Check for SP/FE combo
                const roles = agentRoles.map(item => item.role);
                const hasSP = roles.includes(TicketAgentRole.SP);
                const hasFE = roles.includes(TicketAgentRole.FE);
                if (hasSP && hasFE) {
                    // Use either (here: choose SP's info, and change role to "SP/FE")
                    const agentSP = agentRoles.find(item => item.role === TicketAgentRole.SP);
                    dedupedAgents.push({
                        ...agentSP,
                        role: 'SP/FE',
                    });
                } else {
                    // Add all roles without deduplication
                    agentRoles.forEach(item => dedupedAgents.push(item));
                }
            });

            // Use Map to still dedupe on role+agent.id for other roles
            const uniqueList = Array.from(new Map(dedupedAgents.map(item => [`${item.role}_${item.agent.id}`, item])).values());

            list.push(...uniqueList);
        }

        return list;
    }, [agents, organization]);

    return (
        <RibbonContainer headerLabel={'Contact'} isCollapsible={isCollapsible} openCardDefault>

            <div className=' col-span-2 gap-3 grid grid-cols-2 justify-around mt-[0.5rem]  '>
                {memoizedAgents?.map((user, i) => {
                    let lastIndex = i === memoizedAgents.length - 1 ? i : 1;
                    let isToShowRanderComponent = memoizedAgents.length % 2 === 0
                    let userLength = memoizedAgents.length
                    return <div
                        key={i}
                        className={`bg-scogolightblue  mt-3 border text-center cursor-pointer rounded-md w-full shadow-md  ${lastIndex % 2 === 0 ? 'col-span-2' : ''
                            }`}
                    >
                        <div className='relative bg-scogoorange '>
                            <p className='bg-scogoorange text-white text-font10 px-2  shadow-md rounded-3px w-full truncate'>{user.role}</p>

                            {isToShowRanderComponent && <p className='ml-auto absolute right-2 text-left text-white' style={{ top: '0rem' }}>
                                {user.renderComponent ? user.renderComponent : <></>}
                            </p>}
                        </div>
                        <div className='mt-1 px-2 ' >
                            {(user?.agent?.id && isLoggedIn) ? <UserPopup
                                userId={user?.agent?.id}
                                anchorOrigin={{
                                    vertical: 'center',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <span className='text-font11  text-scogoprimary pt-6 font-semibol max-w-5/7 m-auto'>
                                    <OverflowTip someLongText={user.agent.display_name} />
                                </span>
                            </UserPopup> : <p className='text-font11 text-scogoprimary font-semibol max-w-5/7 m-auto'>
                                <OverflowTip someLongText={user.agent.display_name} />
                            </p>}
                            <div className='flex justify-center relative'>
                                <p className='text-font10 font-normal text-scogogray' onClick={(e) => {
                                    dispatch(copyToClipboard({ data: user.agent.phone_number }));
                                }}>{user.agent.phone_number}</p>
                                {userLength % 2 === 1 && <p className='ml-auto absolute right-2 text-left text-font11 hover:text-scogoorange text-scogoprimary'>
                                    {user.renderComponent ? user.renderComponent : <></>}
                                </p>}
                            </div>

                        </div>
                    </div>
                })}
            </div>

        </RibbonContainer>

    );
}

export const RenderBasicDetails = ({ serviceRequest }) => {
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);

    const disabledClass = 'text-black flex items-center justify-center rounded-6px shadow-primaryBtnShadow px-4 text-xl gap-1 font-medium relative cursor-not-allowed border-scogoddd bg-scogoddd m-auto py-1';

    const isSowNotFoundLoading = false;

    const onSignOfflick = () => {
        if (serviceRequest.metadata && serviceRequest.metadata.sign_off_template_id) {
            dispatch(generateSignoffReport({ ticketId: serviceRequest.id, formName: 'generateSignoffReport' }));
        } else {
            window.open(signoff_sample.value.link, '_blank')
        }
    }

    const scogoReport = () => {
        dispatch(generateTicketReport({ ticketId: serviceRequest.id, formName: 'generateTicketReport' }));
    }

    const onRegenerateReportCLick = () => {
        console.log('onRegenerateReportCLick');
    }


    const viewHistory = () => {
        let modalComponent = <ViewAllRemark closeModal={() => dispatch(closeModalAction())} ticketId={serviceRequest.id} source='ticket_table' remarkList={serviceRequest.remarks} callHistory={true} />
        let modalWidth = '60em',
            modalHeight = '70vh';
        let head = 'History';
        dispatch(openCustomModal({ modalComponent, heading: head, modalWidth, modalHeight }));
    };

    const sow = serviceRequest.attributes?.find((item) => item.key === 'sow');
    const report = isMilestoneStatusInRange(serviceRequest.milestones, MilestoneSubStatus.SIGN_OFF_REQUESTED);
    const signoff_sample = !report && serviceRequest.attributes?.find((item) => item.key === 'signoff_sample');
    const hasSignoffSample = Boolean(signoff_sample || serviceRequest.metadata?.sign_off_template_id);
    const hasPavMilestone = (Array.isArray(serviceRequest.milestones) ? serviceRequest.milestones : []).some(isPavMilestone);

    const execution_dates = useMemo(() => {
        let dates = [];
        serviceRequest.milestones?.map(({ execution_dates }) => {
            dates.push(...execution_dates);
        });
        return dates;
    }, [serviceRequest.milestones]);

    return (
        <div className=' relative '>
            <RibbonContainer headerLabel={'Basic Info'} isCollapsible>

                <div className='absolute right-2 flex gap-x-1 top-4 '>
                    {sow && <IconToolTip title='SOW'>
                        <span>
                            <ButtonScogoOrange disabledClass={disabledClass} loading={isSowNotFoundLoading} textOrComponent={'SOW'} onClick={() => window.open(sow.value.link, '_blank')} />
                        </span>
                    </IconToolTip>
                    }
                    {hasSignoffSample && <IconToolTip title='Sign Off Sample'>
                        <span>
                            <ButtonScogoOrange disabledClass={disabledClass} loading={formButtonLoading['generateSignoffReport']} textOrComponent={'Sign Off Sample'} onClick={onSignOfflick} />
                        </span>
                    </IconToolTip>}
                    {report && <IconToolTip title='Scogo Report'>
                        <span>
                            <ButtonScogoOrange disabledClass={disabledClass} loading={formButtonLoading['generateTicketReport']} textOrComponent={'Scogo Report'} onClick={() => scogoReport(serviceRequest.id)} />
                        </span>
                    </IconToolTip>}
                    {hasPavMilestone && <IconToolTip title='PAV Report'>
                        <span>
                            <ButtonScogoOrange disabledClass={disabledClass} textOrComponent={'PAV Report'} onClick={() => dispatch(openPavReportModal(serviceRequest))} />
                        </span>
                    </IconToolTip>}
                </div>

                <div className='w-full gap-x-2 grid grid-cols-2 gap-y-2 justify-around  col-span-2 mt-14'>
                    {serviceRequest?.project?.name && (
                        <OverflowTip someLongText={` ${serviceRequest.project.name}`} textClass="text-font10 font-normal text-scogograytext-scogoprimary" />

                    )}
                    <div className='text-center '>
                        {(serviceRequest.use_case_name || serviceRequest.service_type) && (
                            <OverflowTip textClass={"text-font11 text-scogoprimary text-center"} someLongText={` ${serviceRequest.use_case_name ? serviceRequest.use_case_name : ""} ${serviceRequest.service_type && serviceRequest.use_case_name ? '/' : ''} ${serviceRequest.service_type && ` ${serviceRequest.service_type} `}`} />

                        )}
                    </div>
                    <div className=' flex w-full   col-span-2'>
                        <p className='flex w-11/12 '>
                            <p className='w-[17%] text-font11  text-scogogray' >Address : </p>
                            <p className='w-[80%] text-font11 '>
                                <OverflowTip
                                    someLongText={`${validateStringValue(serviceRequest?.location?.address)}`}
                                />
                            </p>
                        </p>

                        <p className='ml-auto px-2'>
                            <IconToolTip placement={'bottom'} title={`Copy Site Address`}>
                                <span
                                    className='cursor-pointer material-icons text-scogobgsky text-lg '
                                    onClick={() =>
                                        dispatch(
                                            copyToClipboard({
                                                data: `${validateStringValue(serviceRequest?.location?.address)}`,
                                            })
                                        )
                                    }
                                >
                                    content_copy
                                </span>
                            </IconToolTip>
                        </p>
                    </div>

                    {serviceRequest?.location?.external_id > 0 && (
                        <div className='col-span-2'>
                            <p className='text-font11 mb-1 text-scogogray'>Site id: {serviceRequest?.location?.external_id}</p>
                            <div className='flex gap-x-2'>
                                <div className='max-w-8/8'>
                                    <OverflowTip someLongText={serviceRequest?.location?.external_id} />
                                </div>
                                <div>
                                    <IconToolTip placement={'bottom'} title={`Copy Site Address`}>
                                        <span
                                            className='cursor-pointer material-icons text-scogobgsky text-lg'
                                            onClick={() =>
                                                dispatch(
                                                    copyToClipboard({
                                                        data: `${serviceRequest?.location?.external_id}`,
                                                    })
                                                )
                                            }
                                        >
                                            content_copy
                                        </span>
                                    </IconToolTip>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className='col-span-2'>
                        {(serviceRequest?.project?.description || serviceRequest?.description) && (
                            <>
                                <p className='text-font11 mb-1 text-scogogray'>Description</p>
                                <LineClampComponent value={serviceRequest?.description ? serviceRequest?.description : serviceRequest?.project?.description} />
                            </>
                        )}
                    </div>

                    <div className='col-span-2  flex flex-col gap-x-7'>
                        <div className='w-full flex items-start gap-4'>
                            <p className='text-font11 mb-1 text-scogogray'>Created At : </p>
                            <div className='flex flex-wrap gap-3 justify-center'>
                                <div
                                    className="bg-scogoLogoGreen text-white px-1 py-1 rounded-lg shadow-md text-center min-w-[6rem] max-w-max"
                                >
                                    <div className="text-font10 font-semibold">
                                        {moment(serviceRequest.created_at).format('DD MMM YY hh:mm A')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='w-full flex items-start gap-4 pt-4'>
                            <span className="text-font11 mb-2 text-scogogray font-medium max-w-max border-scogoorange">Execution</span>
                            <span className='flex '>
                                {execution_dates && execution_dates.length > 0 ? (
                                    <div className="flex flex-wrap max-w-max gap-3">
                                        {execution_dates.map(({ date }, idx) => (
                                            <div
                                                key={idx}
                                                className="bg-scogoLogoGreen text-white px-1 py-1 rounded-lg shadow-md text-center min-w-[6rem] max-w-max"
                                            >
                                                <div className="text-font10 font-semibold">
                                                    {moment(date).format('DD MMM YY hh:mm A')}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </span>
                        </div>


                        {/* {validateDateValue(serviceRequest.sign_off_date) && (
                            <div>
                                <p className='text-font11 mb-1 text-scogogray'>Sign Off</p>
                                <span className='bg-scogoLogoGreen text-white max-w-max m-auto text-font10 px-2 py-1 rounded-3px'>
                                    {moment(serviceRequest.sign_off_date).format('DD MMM YY')}
                                </span>
                            </div>
                        )} */}
                        {/* {serviceRequest.resolution_time && (
                            <div>
                                <p className='text-font11 mb-1 text-scogogray'>Resolved In</p>
                                <span className='bg-scogoLogoGreen text-white max-w-max m-auto text-font10 px-2 py-1 rounded-3px'>{serviceRequest.resolution_time}</span>
                            </div>
                        )} */}
                    </div>

                    <div className='col-span-2'>
                        <h3 className={'text-scogo99 text-font11 font-medium truncate'}>Remark </h3>
                        <div className='flex-wrap flex items-center line clamp-2 gap-x-2'>
                            {serviceRequest.remarks?.length > 0 ? (
                                <>
                                    <div className='max-w-8/8 truncate'>
                                        {Array.isArray(serviceRequest.remarks) &&
                                            serviceRequest.remarks?.slice(0, 1)?.map((remark) => {
                                                let someLongText = (remark?.remark?.length > 0 ? remark?.remark : '') + (remark?.event?.length > 0 ? remark?.event : '');
                                                return <OverflowTip key={remark?.id} someLongText={someLongText} />;
                                            })}
                                    </div>
                                    {serviceRequest.remarks?.length > 1 && (
                                        <IconToolTip title='View all'>
                                            <span
                                                className='text-font12 round font-normal text-scogoorange cursor-pointer hover:text-scogobgsky'
                                                onClick={() => {
                                                    viewHistory();
                                                }}
                                            >
                                                +{serviceRequest.remarks?.length - 1}
                                            </span>
                                        </IconToolTip>
                                    )}
                                </>
                            ) : (
                                <>No Remark</>
                            )}
                        </div>
                    </div>

                    <div className='col-span-2'>
                        <h3 className={'text-scogo99 text-font11 font-medium truncate mb-1'}>BOQ </h3>

                        <div className='flex-wrap flex items-center  gap-x-1 '>
                            {serviceRequest.assets?.slice(0, 4)?.map(({ asset }) => {
                                const status = `${asset?.status?.length > 0 ? ` ${asset?.status}` : ''}`;
                                const workingStatus = `${asset?.working_condition?.length > 0 ? `, ${asset?.working_condition}` : ''}`;
                                return (
                                    <IconToolTip key={asset?.id} title={`${status} ${workingStatus}`} >
                                        <span className={`bg-${getAssetColor(asset)} cursor-pointer text-white max-w-12   text-font10 px-2 flex items-center rounded-3px mb-1`}>
                                            <span className='max-w-4/5 truncate'  >
                                                {(asset?.serial_number?.length > 0 ? asset?.serial_number : asset?.title)}
                                            </span>
                                            <span>
                                                {(asset?.count > 1 ? `: ${asset?.count}` : '')}
                                            </span>
                                            <span className="material-icons text-font12 ml-1 hover:text-scogobgsky" onClick={(event) => {
                                                event.stopPropagation();
                                                const text = asset?.serial_number?.length > 0 ? asset?.serial_number : asset?.title

                                                dispatch(copyToClipboard({ data: text }))
                                            }}>
                                                content_copy
                                            </span>
                                        </span>
                                    </IconToolTip>
                                );
                            })}

                            {serviceRequest.assets?.length > 4 && (
                                <>

                                    <AssetPopup assetList={serviceRequest.assets?.slice(4)} anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left',
                                        }} >
                                        <span className='text-font12 round font-normal text-scogoorange relative cursor-pointer hover:text-scogobgsky countOfBOQ'>
                                            +{serviceRequest.assets?.length - 4}
                                        </span>
                                    </AssetPopup>

                                </>
                            )}
                        </div>
                    </div>
                </div>
            </RibbonContainer>
        </div>
    )
}

const RenderPaymentDetails = ({ serviceRequest }) => {
    const [openCard, setOpenCard] = useState(true);
    const dispatch = useDispatch();
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);

    const priceArray = useMemo(() => {
        const customer = {
            title: 'Customer',
            price: 0,
            isCardVisible: true,
        };
        const sp = {
            title: 'SP',
            price: 0,
            isCardVisible: true,
        };
        const cluster = {
            title: 'CLUSTER',
            price: 0,
            isCardVisible: true,
        };
        const psp = {
            title: 'PSP',
            price: 0,
            isCardVisible: true,
        };
        const warehouse = {
            title: 'Warehouse Owner',
            price: 0,
            isCardVisible: true,
        };
        const list = [];

        const isScogoMember = newLoggedUserData.orgMemberships.find(({ id }) => id === ScogoID);
        const isSameOrgTicket = newLoggedUserData.orgMemberships.find(({ id }) => id === serviceRequest.helpdesk_id)
        const findPsp = serviceRequest?.agents?.find((agent) => agent.role === TicketAgentRole.PSP);
        const findCluster = serviceRequest?.agents?.find((agent) => agent.role === TicketAgentRole.CLUSTER);
        const findWarehouse = serviceRequest?.agents?.find((agent) => agent.role === TicketAgentRole.WAREHOUSE_OWNER);

        const isLoginUserIsSP = newLoggedUserData.user.roles.find((elem) => elem.label === 'SP');

        if (canViewCustomerBillingTicket(serviceRequest) && isScogoMember) {
            list.push(customer);
        };

        if (isScogoMember) {
            customer.price += convertIntoRupee(serviceRequest.customer_cost);

            if (serviceRequest.milestones?.length > 0) {
                serviceRequest.milestones.forEach(({ payments, id, type, milestone_status }) => {
                    if (!payments) return;
                    if (milestone_status === MilestoneSubStatus.HARD_CLOSED) return;

                    const mileStoneSp = serviceRequest?.agents?.find((ag) => ag.role === TicketAgentRole.SP && id === ag.milestone_id);

                    if (isLoginUserIsSP && mileStoneSp) {
                        const isAccepted = mileStoneSp.agent.id === newLoggedUserData.user.id;
                        if (isAccepted) {
                            sp.price += convertIntoRupee(payments.SP_SERVICE);

                            // if (!findPsp && payments?.PSP_INCENTIVE > 0 && type !== MilestoneType.RMA) {
                            //     sp.price += convertIntoRupee(payments.PSP_INCENTIVE);
                            // };


                            if (mileStoneSp && !isNaN(mileStoneSp.ticket_payment_ratio)) {
                                sp.price *= mileStoneSp.ticket_payment_ratio;
                            };
                        };
                    } else {
                        if (payments?.SP_SERVICE > 0) {
                            sp.price += convertIntoRupee(payments.SP_SERVICE);
                        }

                        // if (!findPsp && payments?.PSP_INCENTIVE > 0 && type !== MilestoneType.RMA) {
                        //     sp.price += convertIntoRupee(payments.PSP_INCENTIVE);
                        // };

                        if (!findPsp && payments?.PSP_INCENTIVE > 0 && type === MilestoneType.RMA) {
                            cluster.price += convertIntoRupee(payments.PSP_INCENTIVE);
                        };

                        if (mileStoneSp && !isNaN(mileStoneSp.ticket_payment_ratio)) {
                            sp.price *= mileStoneSp.ticket_payment_ratio;
                        }
                    };

                    if (findPsp && payments?.PSP_INCENTIVE > 0) {
                        psp.price += convertIntoRupee(payments.PSP_INCENTIVE);
                    };

                    if (findCluster && payments?.CLUSTER_INCENTIVE > 0) {
                        cluster.price += convertIntoRupee(payments.CLUSTER_INCENTIVE);
                    };

                    if (findWarehouse && payments?.WAREHOUSE_OWNER_INCENTIVE > 0) {
                        warehouse.price += convertIntoRupee(payments.WAREHOUSE_OWNER_INCENTIVE);
                    }
                })
            }

        };

        if (!isScogoMember && serviceRequest.helpdesk_id === ScogoID && serviceRequest.org_id === newLoggedUserData.org.id) {
            customer.price += convertIntoRupee(serviceRequest.customer_cost);
        }

        if (Array.isArray(serviceRequest.milestone_wise_additional_approved_amount)) {
            serviceRequest.milestone_wise_additional_approved_amount.forEach((additonal_amount) => {

                if (isLoginUserIsSP) {
                    const isAccepted = serviceRequest?.agents?.find((ag) => ag.role === TicketAgentRole.SP && additonal_amount.milestone_id === ag.milestone_id && ag.agent.id === newLoggedUserData.user.id);
                    if (isAccepted) {
                        sp.price += convertIntoRupee(additonal_amount.vendor);
                    }
                } else {
                    sp.price += convertIntoRupee(additonal_amount.vendor);
                }
                cluster.price += convertIntoRupee(additonal_amount.incentive);
                customer.price += (convertIntoRupee(additonal_amount.customer) * -1);
            })
        }


        if (isSameOrgTicket) {
            list.push(sp);
        }

        if (canViewIncentiveTicket(serviceRequest) && isSameOrgTicket && isScogoMember) {
            if (findCluster) list.push(cluster);
            if (findPsp) list.push(psp);
        };
        if (canViewIncentiveTicket(serviceRequest) && isSameOrgTicket && isScogoMember && findWarehouse) list.push(warehouse)
        return list;
    }, [serviceRequest, newLoggedUserData.orgMemberships, newLoggedUserData.user.roles, newLoggedUserData.user.id, newLoggedUserData.org.id]);

    const openVendorpaymentDetail = () => {
        let heading = 'Payments';
        let modalComponent = <PaymentsDetails ticket={serviceRequest} ticketId={serviceRequest.id} />;
        let modalWidth = '80rem',
            modalHeight = 'auto';
        dispatch(openCustomModal({ modalComponent, heading, modalWidth, modalHeight }));
    }

    return (
        <div
            className={`w-full  border pb-3 p-2 mt-4 bg-white  ${openCard ? 'h-auto' : 'h-16 overflow-hidden '} `}
            style={{
                'box-shadow': ' 0px 3px 6px #00000029',
                'border-radius': '10px',
            }}
        >
            <div
                className='ribbon col-span-2'
                onClick={() => {
                    setOpenCard(!openCard);
                }}
            >
                <span className='ribbon3 bg-scogobgsky ribbonblue'>Payment</span>
                <div className='absolute right-2 flex gap-x-4 top-1'>
                    {priceArray?.length > 1 && <div className='flex items-center'>
                        <SpanLabelButton onClick={openVendorpaymentDetail} label={'View All'} />
                    </div>
                    }
                    <IconToolTip title={openCard ? 'Close' : 'Open'}>
                        <span className='material-icons flex cursor-pointer text-font24  mr-4 text-scogoprimary  hover:text-scogoorange'>
                            {openCard ? 'keyboard_arrow_up' : 'expand_more'}
                        </span>
                    </IconToolTip>
                </div>
            </div>
            <div className='gap-3 grid grid-cols-2 justify-around'>
                {priceArray.map((item, i) => {
                    const lastIndex = i === priceArray.length - 1 ? i : 1;
                    return item.isCardVisible ? (
                        <div
                            key={item?.id}
                            onClick={openVendorpaymentDetail}
                            className={`bg-scogolightblue flex py-4 mt-3 px-2 border justify-between rounded-md shadow-md zoom ${lastIndex % 2 === 0 ? 'col-span-2' : ''}`}
                        >
                            <p className='text-font13 '>{item?.title}</p>
                            <p className='text-font13 font-semibold'>₹ {item?.price}</p>
                        </div>
                    ) : (
                        <></>
                    );
                })}
            </div>
        </div>
    )
}

export const RibbonContainer = ({ children, headerLabel, isCollapsible = false, openCardDefault = true, padding = 'p-2', childrenClass = 'justify-around -mt-[0.7rem] w-full' }) => {

    const [openCard, setOpenCard] = useState(openCardDefault);
    return (<>  <div
        className={`relative w-full border pb-3 p-2 bg-white`}
        style={{
            'box-shadow': ' 0px 3px 6px #00000029',
            'border-radius': '10px',
        }}
    >
        <div
            className='ribbon col-span-2'

        >
            <span className='ribbon3 bg-scogobgsky ribbonblue'>{headerLabel}</span>
            {isCollapsible && <div className='absolute right-2 flex gap-x-3 top-1' onClick={() => { setOpenCard(!openCard) }}>
                <IconToolTip title={openCard ? 'Close' : ' Open'}>
                    <span className='material-icons flex cursor-pointer text-font24  mr-4 text-scogoprimary  hover:text-scogoorange'>
                        {openCard ? 'keyboard_arrow_up' : 'expand_more'}
                    </span>
                </IconToolTip>
            </div>}
        </div>
        {openCard && <div className={childrenClass}>

            {children}
        </div>}
    </div>
    </>)
}
